import type React from "react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { selectCurrentBuilding } from "@/features/cartography/cartographySlice.selectors";
import { selectConfigItem } from "@/features/config/configSlice";
import { selectPoiCategories } from "../app/appSlice";

import { useAppSelector } from "@/app/hooks";
import { WASMRoutesCalculator } from "@/features/native_routes/routeCalculatorWASM";
import type { Building, PoiCategories } from "@/libs/models";
import { NATIVE_ROUTES_WORKER_ID } from "../worker/constants";
import { useWorkerFactory, WorkerStatus } from "../worker/WorkerProvider";
import { WORKER_EVENTS } from "./types";
import NativeRoutesWorker from "./worker?worker";

export enum NativeRoutesLibState {
  INITIALIZING = "INITIALIZING",
  SETTING_UP = "SETTING_UP",
  READY = "READY",
}

export type NativeRoutesContextType = {
  routesCalculator: WASMRoutesCalculator | null;
  state: NativeRoutesLibState;
  setState: React.Dispatch<
    React.SetStateAction<NativeRoutesContextType["state"]>
  >;
  initNativeLibrary: ({
    building,
    poiCategories,
    flatEarth,
  }: {
    building: Building;
    poiCategories: PoiCategories;
    flatEarth: boolean;
  }) => Promise<NativeRoutesContextType["routesCalculator"]>;
};

export const NativeRoutesContext =
  createContext<NativeRoutesContextType | null>(null);

export const useNativeRoutes = () => {
  const value = useContext(NativeRoutesContext);
  if (!value) {
    throw new Error(
      "useNativeRoutes must be used within a <NativeRoutesProvider/>",
    );
  }
  return value;
};

export const NativeRoutesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const currentBuilding = useAppSelector(selectCurrentBuilding);
  const flatEarth = useAppSelector(
    selectConfigItem("feature_flags.flat_earth"),
  );
  const poiCategories = useAppSelector(selectPoiCategories);

  // State
  const [routesCalculator, setRoutesCalculator] =
    useState<WASMRoutesCalculator | null>(null);
  const [libraryState, setLibraryState] = useState<NativeRoutesLibState>(
    NativeRoutesLibState.INITIALIZING,
  );

  // Hooks
  const { initWorker } = useWorkerFactory();

  const initNativeLibrary = useCallback(
    async ({
      building,
      poiCategories,
      flatEarth,
    }: {
      building: Building;
      poiCategories: PoiCategories;
      flatEarth: boolean;
    }) => {
      if (!building?.pois || !poiCategories) return;

      // If already initialized for current building
      if (routesCalculator?.building?.id == building?.id) return;

      // TODO: this is ñapa because outdoor pois make core library crash, review another way to do it
      const filteredPois = building.pois.filter(
        (poi) => poi.position.x !== undefined && poi.position.x !== null,
      );
      building.pois = filteredPois;
      try {
        const worker = await initWorker({
          id: NATIVE_ROUTES_WORKER_ID,
          onRegistered: async (worker) => {
            return worker
              .postMessageToWorker({
                payload: { building, flatEarth, poiCategories },
                type: WORKER_EVENTS.INIT,
              })
              .then(() => {
                return WorkerStatus.READY;
              });
          },
          persistent: true,
          workerClass: NativeRoutesWorker,
        });
        const lib = new WASMRoutesCalculator(building, worker);
        setRoutesCalculator(lib);
        return lib;
      } catch (e) {
        console.error("Error loding routes library", e);
      } finally {
        setLibraryState(NativeRoutesLibState.SETTING_UP);
      }
    },
    [],
  );

  useEffect(() => {
    setLibraryState(NativeRoutesLibState.INITIALIZING);
    initNativeLibrary({ building: currentBuilding, flatEarth, poiCategories });

    return () => {
      setRoutesCalculator(null);
    };
  }, [
    currentBuilding?.id,
    currentBuilding?.floors,
    currentBuilding?.pois,
    currentBuilding?.geometry,
    currentBuilding?.paths,
    currentBuilding?.geofences,
    poiCategories,
    flatEarth,
  ]);

  return (
    <NativeRoutesContext.Provider
      value={{
        initNativeLibrary,
        routesCalculator,
        setState: setLibraryState,
        state: libraryState,
      }}
    >
      {children}
    </NativeRoutesContext.Provider>
  );
};
